'use client';

import { ReactNode } from 'react';

import {
    Align,
    Box,
    BoxContent,
    DirectionEnum,
    FlexBox,
    GapSize,
    Justify,
    Price,
    PriceLevel,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { PlanPrice } from '@services/products';

import { MobilePlanType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';

import { MarketingType, PlanTypeId } from '@constants/plan';

import { OfferCardPepites } from 'src/app/(offer-wall)/_components/offer-card-pepites';

interface PlanDetailsBoxProps {
    plan: MobilePlanType;
    prices?: PlanPrice;
    onOpenModal: () => void;
    children?: ReactNode;
    showHeader?: boolean;
    isRenewal?: boolean;
}

export function PlanDetailsBox({
    plan,
    prices,
    onOpenModal,
    children,
    showHeader = true,
    isRenewal,
}: Readonly<PlanDetailsBoxProps>) {
    const oldAmount = prices?.final !== prices?.forever ? prices?.forever : undefined;
    const planTitle = isRenewal ? `Forfait ${plan.renewalLabelDataEnvelope}` : plan.sexyName;

    const isSmallPrice = plan.typeId === PlanTypeId.PREMIUM_MULTILIGNE || plan.typeId === PlanTypeId.SIMO_MULTILIGNE;

    const discounts = plan.details?.discounts ?? [];
    const homeDiscount = discounts.find((d) => d.marketingTypes?.includes(MarketingType.HOME) && !d.isIncitation);
    const multilineDiscount = discounts.find((d) => d.marketingTypes?.includes(MarketingType.MULTILINE));
    const shouldShowBboxMention = !isRenewal && (isSmallPrice ? multilineDiscount : homeDiscount);
    const bboxMentionLabel = isSmallPrice ? 'Prix client Bbox + forfait' : 'Prix client Bbox';

    const prospectStrikedAmount =
        prices?.striked !== undefined && prices.striked !== prices.final ? prices.striked : undefined;

    return (
        <Box shadowless>
            <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                {showHeader && (
                    <>
                        <FlexBox justify={Justify.SPACE_BETWEEN} align={Align.CENTER}>
                            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.ONE}>
                                <Title typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TitleLevels.FOUR} marginless>
                                    {planTitle}
                                </Title>
                                <div>
                                    {!isRenewal && oldAmount && (
                                        <Text level={TextLevels.THREE} marginless>
                                            Puis{' '}
                                            <InlinePrice
                                                amount={prices?.forever}
                                                period='mois'
                                                level={TextLevels.THREE}
                                                typo={TypographyBold.TEXT_WEIGHT_NORMAL}
                                            />{' '}
                                            après {prices?.promoDuration} mois
                                        </Text>
                                    )}

                                    <Text level={TextLevels.THREE} className='capitalize-first' marginless>
                                        {plan.obligation ? plan.obligationLabel : 'Sans engagement'}
                                    </Text>

                                    {shouldShowBboxMention && (
                                        <Text level={TextLevels.THREE} marginless>
                                            {bboxMentionLabel}
                                        </Text>
                                    )}
                                </div>
                            </FlexBox>
                            <FlexBox align={Align.CENTER}>
                                <Price
                                    oldAmount={prospectStrikedAmount ?? oldAmount}
                                    amount={prices?.final}
                                    period='mois'
                                    level={PriceLevel.SIX}
                                />
                            </FlexBox>
                        </FlexBox>
                        <Spacer size={SpacerSize.FOUR} />
                    </>
                )}

                {children}
                <OfferCardPepites offer={plan} />
                <Text level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED}>
                    <button type='button' className='link button-clear' onClick={onOpenModal}>
                        Voir plus de détails
                    </button>
                </Text>
            </BoxContent>
        </Box>
    );
}
